import { useEffect, useState } from 'react';

import { FastAverageColor, FastAverageColorOptions } from 'fast-average-color';

import { RGBTuple } from './useBestColorForBackgroundColor/types';
import useBestColorForBackgroundColor from './useBestColorForBackgroundColor/useBestColorForBackgroundColor';

const fac = new FastAverageColor();
const facOptions: FastAverageColorOptions = {
    crossOrigin: 'anonymous',
};

type Details = {
    dominantImageAColor?: RGBTuple;
    dominantImageBColor?: RGBTuple;
    imageAContrast: number;
    imageBContrast: number;
};

export type BestImageAndDetails = [string | null, Details | null];

const useBestImageForBackgroundColor = (
    imageAUrl?: string,
    imageBUrl?: string,
    backgroundColor?: string
): BestImageAndDetails => {
    const [dominantImageAColor, setDominantImageAColor] = useState<string>();
    const [dominantImageBColor, setDominantImageBColor] = useState<string>();

    useEffect(() => {
        const getImageAColor = async () => {
            // Using a query parameter to avoid reusing a cached non-cross-origin request. This should force a
            // cross-origin request and allow that request to be cached.
            const color = await fac.getColorAsync(`${imageAUrl}?q=1`, facOptions);
            setDominantImageAColor(color.hex);
        };

        if (imageAUrl !== undefined) {
            getImageAColor();
        }
    }, [imageAUrl]);

    useEffect(() => {
        const getImageBColor = async () => {
            const color = await fac.getColorAsync(`${imageBUrl}?q=1`, facOptions);
            setDominantImageBColor(color.hex);
        };

        if (imageBUrl !== undefined) {
            getImageBColor();
        }
    }, [imageBUrl]);

    const bestColorDetails = useBestColorForBackgroundColor(dominantImageAColor, dominantImageBColor, backgroundColor);

    if (bestColorDetails[0] === null || bestColorDetails[1] === null) {
        return [null, null];
    }

    return [
        (bestColorDetails[0] === dominantImageAColor ? imageAUrl : imageBUrl) || null,
        {
            dominantImageAColor: bestColorDetails[1].colorA,
            dominantImageBColor: bestColorDetails[1].colorB,
            imageAContrast: bestColorDetails[1].colorAContrast,
            imageBContrast: bestColorDetails[1].colorBContrast,
        },
    ];
};

export default useBestImageForBackgroundColor;
